(() => {
  'use strict';

  const namespaceKey = 'GuruShopHomepageShipping';
  const markerSelector = 'script[data-guru-homepage-shipping-root]';
  const evidenceSelector = '[data-guru-shipping-evidence]';
  const existingApi = window[namespaceKey];

  if (existingApi?.scan) {
    existingApi.scan(document);
    return;
  }

  const MAX_BODY_BYTES = 1_000_000;
  const MAX_CART_ITEMS = 1_000;
  const MAX_MINOR_UNITS = 1_000_000_000_000;
  const REQUEST_TIMEOUT_MS = 5_000;
  const initializedRoots = new WeakSet();
  const cleanupByRoot = new WeakMap();
  const activeRoots = new Set();
  let commerceAbortController = null;
  let activeRequest = null;
  let requestGeneration = 0;
  let lastSnapshot = null;
  let refreshAttempted = false;

  const evidenceWithin = (root) => [...root.querySelectorAll(evidenceSelector)];

  const hideRoot = (root) => {
    evidenceWithin(root).forEach((evidence) => {
      evidence.hidden = true;
    });
  };

  const hideAll = () => activeRoots.forEach(hideRoot);

  const strictPositiveInteger = (value) => {
    if (typeof value !== 'string' || !/^[1-9]\d{0,12}$/.test(value)) return null;
    const number = Number(value);
    return Number.isSafeInteger(number) && number <= MAX_MINOR_UNITS ? number : null;
  };

  const strictMinorAmount = (value) => (
    Number.isSafeInteger(value) && value >= 0 && value <= MAX_MINOR_UNITS ? value : null
  );

  const validateCart = (cart) => {
    if (!cart || typeof cart !== 'object' || Array.isArray(cart)) return null;
    if (typeof cart.currency !== 'string' || !/^[A-Z]{3}$/.test(cart.currency)) return null;
    if (typeof cart.requires_shipping !== 'boolean') return null;
    if (!Array.isArray(cart.items) || cart.items.length > MAX_CART_ITEMS) return null;

    let spend = 0;
    let hasShippableLine = false;

    for (const item of cart.items) {
      if (!item || typeof item !== 'object' || Array.isArray(item) || typeof item.requires_shipping !== 'boolean') return null;
      if (!item.requires_shipping) continue;

      hasShippableLine = true;
      const originalLinePrice = strictMinorAmount(item.original_line_price);
      if (originalLinePrice === null || !Array.isArray(item.discount_allocations) || item.discount_allocations.length > MAX_CART_ITEMS) return null;

      let lineDiscount = 0;
      for (const allocation of item.discount_allocations) {
        if (!allocation || typeof allocation !== 'object' || Array.isArray(allocation)) return null;
        const amount = strictMinorAmount(allocation.amount);
        if (amount === null || lineDiscount > originalLinePrice - amount) return null;
        lineDiscount += amount;
      }

      const lineNet = originalLinePrice - lineDiscount;
      if (spend > MAX_MINOR_UNITS - lineNet) return null;
      spend += lineNet;
    }

    if (cart.requires_shipping !== hasShippableLine) return null;
    return { currency: cart.currency, requiresShipping: cart.requires_shipping, spend };
  };

  const publishRoot = (root, snapshot) => {
    evidenceWithin(root).forEach((evidence) => {
      const threshold = strictPositiveInteger(evidence.dataset.shippingThreshold);
      const expectedCurrency = evidence.dataset.shippingCurrency;
      const currencyValid = typeof expectedCurrency === 'string' && /^[A-Z]{3}$/.test(expectedCurrency);
      if (!snapshot?.requiresShipping || threshold === null || !currencyValid || snapshot.currency !== expectedCurrency) {
        evidence.hidden = true;
        return;
      }

      const qualifies = snapshot.spend >= threshold;
      const stateLabel = qualifies ? evidence.dataset.shippingReachedLabel : evidence.dataset.shippingBelowLabel;
      const accessibleLabel = qualifies ? evidence.dataset.shippingReachedAria : evidence.dataset.shippingBelowAria;
      const stateNode = evidence.querySelector('[data-guru-shipping-state]');
      if (!stateNode || !stateLabel || !accessibleLabel) {
        evidence.hidden = true;
        return;
      }

      stateNode.textContent = stateLabel;
      evidence.setAttribute('aria-label', accessibleLabel);
      evidence.dataset.shippingSpend = String(snapshot.spend);
      evidence.dataset.shippingQualifies = String(qualifies);
      evidence.hidden = false;
    });
  };

  const publishAll = (snapshot) => activeRoots.forEach((root) => publishRoot(root, snapshot));

  const cartUrl = () => {
    const routeRoot = window.Shopify?.routes?.root || '/';
    if (typeof routeRoot !== 'string' || !routeRoot.startsWith('/') || routeRoot.startsWith('//') || /[\\?#\u0000-\u001f\u007f]/.test(routeRoot)) return null;
    try {
      const url = new URL(`${routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`}cart.js`, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.endsWith('/cart.js') || url.search || url.hash) return null;
      return url.href;
    } catch (_error) {
      return null;
    }
  };

  const readBoundedJson = async (response) => {
    const contentType = response.headers.get('content-type') || '';
    if (!/^application\/json(?:\s*;|$)/i.test(contentType)) throw new Error('Unexpected cart content type');

    const contentLength = response.headers.get('content-length');
    if (contentLength !== null) {
      if (!/^\d+$/.test(contentLength) || Number(contentLength) > MAX_BODY_BYTES) throw new Error('Invalid cart content length');
    }

    if (!response.body?.getReader) {
      const text = await response.text();
      if (new TextEncoder().encode(text).byteLength > MAX_BODY_BYTES) throw new Error('Cart response too large');
      return JSON.parse(text);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let byteLength = 0;
    let text = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      byteLength += value.byteLength;
      if (byteLength > MAX_BODY_BYTES) {
        reader.cancel().catch(() => {});
        throw new Error('Cart response too large');
      }
      text += decoder.decode(value, { stream: true });
    }
    text += decoder.decode();
    return JSON.parse(text);
  };

  const pruneDisconnectedRoots = () => {
    for (const root of [...activeRoots]) {
      if (!root.isConnected) cleanupRoot(root);
    }
  };

  const refresh = async () => {
    pruneDisconnectedRoots();
    if (activeRoots.size === 0) return false;

    const generation = ++requestGeneration;
    activeRequest?.abort();
    activeRequest = null;
    refreshAttempted = true;
    lastSnapshot = null;
    hideAll();
    const url = cartUrl();
    if (!url) return false;

    const controller = new AbortController();
    activeRequest = controller;
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (response.status !== 200) throw new Error('Cart request failed');
      const cart = await readBoundedJson(response);
      const snapshot = validateCart(cart);
      if (!snapshot) throw new Error('Malformed cart response');
      if (controller.signal.aborted || generation !== requestGeneration || activeRoots.size === 0) return false;
      lastSnapshot = snapshot;
      publishAll(snapshot);
      return true;
    } catch (_error) {
      return false;
    } finally {
      window.clearTimeout(timeout);
      if (activeRequest === controller) activeRequest = null;
    }
  };

  const startCommerceLifecycle = () => {
    if (commerceAbortController) return;
    commerceAbortController = new AbortController();
    const { signal } = commerceAbortController;
    document.addEventListener('cart:change', refresh, { signal });
    document.addEventListener('cart:refresh', refresh, { signal });
    window.addEventListener('pageshow', refresh, { signal });
  };

  const stopCommerceLifecycle = () => {
    commerceAbortController?.abort();
    commerceAbortController = null;
    requestGeneration += 1;
    activeRequest?.abort();
    activeRequest = null;
    lastSnapshot = null;
    refreshAttempted = false;
  };

  const initRoot = (sectionRoot) => {
    if (!(sectionRoot instanceof HTMLElement) || initializedRoots.has(sectionRoot) || evidenceWithin(sectionRoot).length === 0) return false;
    const startsNewLifecycle = activeRoots.size === 0;
    initializedRoots.add(sectionRoot);
    activeRoots.add(sectionRoot);
    sectionRoot.dataset.guruHomepageShippingBound = 'true';

    if (startsNewLifecycle) hideRoot(sectionRoot);
    else if (lastSnapshot) publishRoot(sectionRoot, lastSnapshot);
    else if (refreshAttempted) hideRoot(sectionRoot);

    cleanupByRoot.set(sectionRoot, () => {
      initializedRoots.delete(sectionRoot);
      activeRoots.delete(sectionRoot);
      cleanupByRoot.delete(sectionRoot);
      delete sectionRoot.dataset.guruHomepageShippingBound;
      if (activeRoots.size === 0) stopCommerceLifecycle();
    });
    startCommerceLifecycle();
    if (startsNewLifecycle) void refresh();
    return true;
  };

  const cleanupRoot = (sectionRoot) => {
    const cleanup = cleanupByRoot.get(sectionRoot);
    if (!cleanup) return false;
    cleanup();
    return true;
  };

  const scan = (scope = document) => {
    const markers = [];
    if (scope.matches?.(markerSelector)) markers.push(scope);
    markers.push(...scope.querySelectorAll?.(markerSelector) || []);
    markers.forEach((marker) => initRoot(document.getElementById(marker.dataset.guruHomepageShippingRoot)));
  };

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanupRoot(event.target));

  window[namespaceKey] = { cleanupRoot, initRoot, refresh, scan };
  scan(document);

  const mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => record.removedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      if (node.matches('[data-guru-homepage-shipping-bound]')) cleanupRoot(node);
      node.querySelectorAll('[data-guru-homepage-shipping-bound]').forEach(cleanupRoot);
    }));
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node instanceof Element) scan(node);
    }));
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
